import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, Megaphone, CheckCircle2, XCircle, ImagePlus, X as XIcon } from "lucide-react";
import { ddmmyyyy } from "../../lib/dates";

const EMPTY = {
    title: "",
    message: "",
    image_url: "",
    active: true,
    expires_at: "",
};

export default function AdminBanners() {
    const [banners, setBanners] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const { data } = await api.get("/admin/banners");
        setBanners(data || []);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (b) => {
        setEditingId(b.id);
        setForm({
            title: b.title || "",
            message: b.message || "",
            image_url: b.image_url || "",
            active: b.active !== false,
            expires_at: b.expires_at ? b.expires_at.slice(0, 10) : "",
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            image_url: form.image_url || null,
            expires_at: form.expires_at || null,
        };
        try {
            if (editingId) {
                await api.put(`/admin/banners/${editingId}`, payload);
                toast.success("Banner updated");
            } else {
                await api.post("/admin/banners", payload);
                toast.success("Banner created");
            }
            setOpen(false); setEditingId(null); setForm(EMPTY); load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this banner?")) return;
        await api.delete(`/admin/banners/${id}`);
        toast.success("Deleted");
        load();
    };

    const toggleActive = async (b) => {
        await api.put(`/admin/banners/${b.id}`, { active: !b.active });
        toast.success(b.active ? "Banner disabled" : "Banner enabled");
        load();
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
            toast.error("Only PNG / JPEG allowed"); return;
        }
        if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm(prev => ({ ...prev, image_url: data.url }));
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const imgSrc = (p) => p?.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`;

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Banner Management</h1>
                        <p className="text-slate-400 text-sm mt-1">Create live announcements shown at the top of the home page. The most recent active &amp; non-expired banner is shown to users.</p>
                    </div>
                    <button onClick={openCreate} data-testid="new-banner-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                        <Plus className="w-4 h-4" /> New Banner
                    </button>
                </div>

                {banners.length === 0 ? (
                    <div className="mt-10 text-center py-16 border border-dashed border-slate-700 rounded-lg" data-testid="banners-empty-state">
                        <Megaphone className="w-10 h-10 mx-auto text-slate-600 mb-3" />
                        <div className="text-sm text-slate-400">No banners yet. Click "New Banner" to create your first announcement.</div>
                    </div>
                ) : (
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {banners.map(b => (
                            <div key={b.id} data-testid={`banner-card-${b.id}`}
                                className={`bg-slate-900 border rounded-lg overflow-hidden ${b.is_live ? "border-[#00FF66]/40" : "border-slate-800"}`}>
                                {b.image_url && (
                                    <div className="relative h-32 overflow-hidden bg-slate-800">
                                        <img src={imgSrc(b.image_url)} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <div className="p-4">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {b.is_live ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">
                                                        <CheckCircle2 className="w-3 h-3" /> Live
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 border border-slate-600">
                                                        <XCircle className="w-3 h-3" /> {b.active ? "Expired" : "Disabled"}
                                                    </span>
                                                )}
                                                {b.expires_at && (
                                                    <span className="text-[10px] uppercase tracking-widest text-slate-500">
                                                        Expires {ddmmyyyy(b.expires_at.slice(0, 10))}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="font-display text-lg font-bold uppercase text-white mt-2 truncate">{b.title}</h3>
                                            <p className="text-sm text-slate-300 mt-1 line-clamp-2">{b.message}</p>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                                        <button onClick={() => toggleActive(b)} data-testid={`banner-toggle-${b.id}`}
                                            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded text-[10px] font-bold uppercase tracking-widest ${
                                                b.active ? "bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25" : "bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40 hover:bg-[#00FF66]/25"
                                            }`}>
                                            {b.active ? "Disable" : "Enable"}
                                        </button>
                                        <button onClick={() => openEdit(b)} data-testid={`banner-edit-${b.id}`}
                                            className="inline-flex items-center gap-1.5 px-3 h-8 bg-slate-200 text-slate-900 rounded text-[10px] font-bold uppercase tracking-widest hover:bg-white">
                                            <Pencil className="w-3 h-3" /> Edit
                                        </button>
                                        <button onClick={() => remove(b.id)} data-testid={`banner-delete-${b.id}`}
                                            className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-[10px] font-bold uppercase tracking-widest">
                                            <Trash2 className="w-3 h-3" /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {open && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
                        <form onSubmit={save} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 my-6 max-h-[90vh] overflow-y-auto" data-testid="banner-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">{editingId ? "Edit Banner" : "New Banner"}</h3>

                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Title</label>
                                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={120}
                                    data-testid="banner-title-input"
                                    className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            </div>

                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Message</label>
                                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={3} maxLength={600}
                                    data-testid="banner-message-input"
                                    className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            </div>

                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Image <span className="text-slate-500 normal-case tracking-normal ml-1">optional</span></label>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Recommended: <b className="text-slate-300">1920×700 px</b> (desktop) / <b className="text-slate-300">1080×1350 px</b> (mobile). Any image is accepted — the system auto-crops &amp; serves an optimized WebP per device.
                                </p>
                                <div className="mt-1 flex items-center gap-3">
                                    {form.image_url && (
                                        <div className="relative w-28 h-16 rounded overflow-hidden border border-slate-700 bg-slate-800 flex-shrink-0">
                                            <img src={imgSrc(form.image_url)} alt="" className="w-full h-full object-cover" />
                                            <button type="button" onClick={() => setForm({ ...form, image_url: "" })}
                                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-slate-950/80 text-slate-300 hover:bg-red-500 hover:text-white flex items-center justify-center"
                                                data-testid="banner-image-remove">
                                                <XIcon className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}
                                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="banner-image-upload"
                                        className="inline-flex items-center gap-2 px-3 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded text-xs font-bold uppercase tracking-widest text-slate-200 disabled:opacity-50">
                                        <ImagePlus className="w-4 h-4" /> {uploading ? "Uploading…" : (form.image_url ? "Replace" : "Upload Image")}
                                    </button>
                                    <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                                        onChange={(e) => handleFile(e.target.files?.[0])} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Expires On</label>
                                    <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                                        data-testid="banner-expires-input"
                                        className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                                    <p className="text-[11px] text-slate-500 mt-1">Leave blank to never expire.</p>
                                </div>
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Active</label>
                                    <div className="mt-1 inline-flex h-10 rounded border border-slate-700 overflow-hidden" role="group">
                                        {[{ v: true, l: "On" }, { v: false, l: "Off" }].map(opt => (
                                            <button type="button" key={String(opt.v)}
                                                onClick={() => setForm({ ...form, active: opt.v })}
                                                data-testid={`banner-active-${opt.l.toLowerCase()}`}
                                                className={`px-4 h-full text-xs font-bold tracking-widest transition ${form.active === opt.v ? "bg-[#00FF66] text-black" : "bg-slate-950 text-slate-400 hover:text-white"}`}>
                                                {opt.l}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" data-testid="banner-save-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">{editingId ? "Update" : "Create"}</button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
